import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import './checkboxes-tree-dropdown.scss';
import InputWithOverlay from './input-with-overlay/input-with-overlay';
import {offset, autoUpdate, useFloating, useInteractions, useDismiss, FloatingPortal, flip, shift, size} from '@floating-ui/react';
import NestedCheckboxesDropdownStore from "./stores/nested-checkboxes-dropdown-store";
import {observer} from "mobx-react";
import CheckboxesTree from "./checkboxes-tree/checkboxes-tree";
import {ICheckboxesTreeItem} from './checkboxes-tree/stores/checkboxes-tree-store';

interface ICheckboxesTreeDropdownProps {
    compressed?: boolean;
    value?: ICheckboxesTreeItem[];
    options: ICheckboxesTreeItem[];
    onChange?: (updatedData: ICheckboxesTreeItem[]|undefined) => void;
    searchInputPlaceholder?: string;
    isInitiallyAllItemsExpanded?: boolean;
    checkboxesSelectAllText?: string;
    isCheckboxesListDisabled?: boolean;
    isSearchInputDisabled?: boolean;
    dropdownHeight?: number | string;
    dropdownMinWidth?: number;
}

/** Displays a searchable dropdown with hierarchical checkboxes */
const CheckboxesTreeDropdown: React.FC<ICheckboxesTreeDropdownProps> = observer(
    ({
        compressed,
        value,
        options,
        onChange,
        searchInputPlaceholder = '',
        isInitiallyAllItemsExpanded,
        checkboxesSelectAllText,
        isCheckboxesListDisabled,
        isSearchInputDisabled,
        dropdownHeight = 300,
        dropdownMinWidth = 350
     }) => {

        const wrapperRef = useRef<HTMLDivElement | null>(null);
        const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
        const dropdownStore = useMemo(() => new NestedCheckboxesDropdownStore(setDebouncedSearchTerm), []);
        const [summaryText, setSummaryText] = useState<string>('');
        const [isAllHidden, setIsAllHidden] = useState<boolean>(false);
        const [isFiltering, setIsFiltering] = useState<boolean>(false);
        const [isDropdownOpen, setIsOpen] = useState(false);
        const floating = useFloating({
            open: isDropdownOpen,
            onOpenChange: setIsOpen,
            whileElementsMounted: autoUpdate,
            placement: 'bottom-start',
            middleware: [
                offset(4),
                flip({
                    fallbackPlacements: ['top-start', 'bottom-start']
                }),
                shift({padding: 8}),
                size({
                    apply: ({elements}) => {
                        const reference = elements.reference as HTMLElement;
                        const referenceWidth = reference ? reference.getBoundingClientRect().width : 0;
                        const targetWidth = Math.max(dropdownMinWidth, referenceWidth);
                        Object.assign(elements.floating.style, {
                            width: `${targetWidth}px`
                        });
                    }
                })
            ],
            strategy: 'fixed'
        });

        /** Close when clicking outside using Floating UI dismiss. */
        const dismiss = useDismiss(floating.context, {outsidePress: true, outsidePressEvent: 'mousedown'});
        const {getFloatingProps} = useInteractions([dismiss]);

        /** Sync input wrapper ref with Floating UI reference node. */
        const setReferenceNodeCombined = useCallback((node: HTMLDivElement | null) => {
            if (wrapperRef) {
                (wrapperRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            }
            floating.refs.setReference(node);
        }, [floating.refs]);

        /** Autofocus the input when the dropdown opens. */
        useEffect(() => {
            if (isDropdownOpen) {
                wrapperRef.current?.querySelector('input')?.focus();
            }
        }, [isDropdownOpen]);

        useEffect(() => {
            if (!isDropdownOpen) {
                dropdownStore.setSearchTerm('');
            }
        }, [isDropdownOpen, dropdownStore]);

        /** Toggle open on wrapper click; icon click toggles, text area opens. */
        const handleToggleIconClick = (e: React.MouseEvent<HTMLDivElement>) => {
            if (isSearchInputDisabled) {
                return;
            }
            const target = e.target as HTMLElement;
            const icon = wrapperRef.current?.querySelector('.input-icon') as HTMLElement | null;
            const nextOpen = icon && icon.contains(target) ? !isDropdownOpen : true;
            setIsOpen(nextOpen);
        };

        /** Update search term in the dropdown store. */
        const handleInputChange = (value: string) => {
            dropdownStore.setSearchTerm(value);
        };

        return (
            <div className={`checkboxes-tree-dropdown ${isDropdownOpen ? 'open' : ''}`}>
                <InputWithOverlay
                    value={dropdownStore.getSearchTerm()}
                    onChange={handleInputChange}
                    summaryText={summaryText}
                    placeholder={searchInputPlaceholder}
                    onMouseDown={handleToggleIconClick}
                    setReferenceNode={setReferenceNodeCombined}
                    isInputDisabled={isSearchInputDisabled}
                />

                <FloatingPortal>
                    <div style={{display: isDropdownOpen ? 'block' : 'none'}}>
                        <div ref={floating.refs.setFloating}
                             style={{
                                 ...floating.floatingStyles,
                                 height: dropdownHeight,
                                 overflowY: (!isFiltering && !isAllHidden) ? 'auto' : 'hidden'
                             }}
                             {...getFloatingProps()}
                             className="floating-checkboxes-tree-dropdown-container"
                        >
                            <CheckboxesTree
                                compressed={compressed}
                                value={value}
                                options={options}
                                onChange={onChange}
                                searchTerm={debouncedSearchTerm}
                                selectAllText={checkboxesSelectAllText}
                                isDisabled={isCheckboxesListDisabled}
                                isInitiallyAllItemsExpanded={Boolean(isInitiallyAllItemsExpanded)}
                                onSummaryTextChange={setSummaryText}
                                onVisibilityChange={setIsAllHidden}
                                onFilteringChange={setIsFiltering}
                            />
                        </div>
                    </div>
                </FloatingPortal>
            </div>
        );
    });

export default CheckboxesTreeDropdown;
